import type { SummaryListItemType } from '@/types'
import { formatHours, formatNumberWithThousandSeparators, formatUsageRatio } from '@/helpers/format'

interface RenderSimplifiedSummaryInput {
  quantityArea: number
  usages: Array<{
    title: string
    ratio: number
  }>
  // SIA 105 landscape architecture values - only present when the project has the landscape role
  landscapeQuantityArea?: number
  landscapeUsages?: Array<{
    title: string
    ratio: number
  }>
  civilEngineerSlabArea?: number | null
  totalHours: number
  // VAL-166: share of the specialist coordination surcharge in totalHours
  coordination?: { title: string; hours: number }
}

type TranslationFn = (key: string) => string
type WidgetTextFn = (...path: string[]) => string

export interface SimplifiedSummaryUsageSection {
  title: string
  items: SummaryListItemType[]
}

export interface SimplifiedSummaryListItems {
  summaryList: SummaryListItemType[]
  summaryUsageList: SummaryListItemType[]
  summaryUsageSections: SimplifiedSummaryUsageSection[]
}

function mapUsageRows(usages: RenderSimplifiedSummaryInput['usages']): SummaryListItemType[] {
  return usages.map((usage) => ({
    title: usage.title,
    value: `${formatUsageRatio(usage.ratio)} %`,
  }))
}

export function renderSimplifiedSummary(
  input: RenderSimplifiedSummaryInput,
  t: TranslationFn,
  wtSummary: WidgetTextFn,
  wtProjectGeneral?: WidgetTextFn
): SimplifiedSummaryListItems {
  const hasLandscapeUsages = Boolean(input.landscapeUsages?.length)
  const hasStandardUsages = Boolean(input.usages.length)
  const hasMixedUsages = hasStandardUsages && hasLandscapeUsages
  const summaryList: SummaryListItemType[] = []
  const standardUsageRows = mapUsageRows(input.usages)
  const landscapeUsageRows = mapUsageRows(input.landscapeUsages ?? [])
  const summaryUsageList = [...standardUsageRows, ...landscapeUsageRows]
  const summaryUsageSections = hasMixedUsages
    ? [
        {
          title: wtProjectGeneral?.('usages', 'discipline', 'standard') ?? '',
          items: standardUsageRows,
        },
        {
          title: wtProjectGeneral?.('usages', 'discipline', 'landscape') ?? '',
          items: landscapeUsageRows,
        },
      ]
    : summaryUsageList.length
      ? [
          {
            title: wtSummary('summary', 'usages', 'title'),
            items: summaryUsageList,
          },
        ]
      : []

  // Floor area only applies to the standard usages; hide it for landscape-only projects
  if (input.usages.length || !hasLandscapeUsages) {
    summaryList.push({
      title: wtSummary('summary', 'overall', 'quantityArea'),
      value: `${formatNumberWithThousandSeparators(input.quantityArea)} ${t('general.sq_m')}`,
      additionalClass: 'dark-title',
    })
  }
  if (hasLandscapeUsages && input.landscapeQuantityArea !== undefined) {
    summaryList.push({
      title: wtSummary('summary', 'overall', 'landscapeQuantityArea'),
      value: `${formatNumberWithThousandSeparators(input.landscapeQuantityArea)} ${t('general.sq_m')}`,
      additionalClass: 'dark-title',
    })
  }
  if (input.civilEngineerSlabArea) {
    summaryList.push({
      title: wtSummary('summary', 'overall', 'civilEngineerSlabArea'),
      value: `${formatNumberWithThousandSeparators(input.civilEngineerSlabArea)} ${t('general.sq_m')}`,
      additionalClass: 'dark-title',
    })
  }
  summaryList.push({
    title: wtSummary('summary', 'overall', 'totalHours'),
    value: `${formatHours(input.totalHours, 0)} ${t('general.hour')}`,
    additionalClass: 'dark-title',
  })
  if (input.coordination?.title) {
    summaryList.push({
      title: input.coordination.title,
      value: `${formatHours(input.coordination.hours, 0)} ${t('general.hour')}`,
    })
  }

  return {
    summaryList,
    summaryUsageList,
    summaryUsageSections,
  }
}
